import styled, { css } from 'styled-components';

import { pickColor } from 'themes/helpers/pickColor';
import { FontScale, getFontScale } from 'themes/mixins/getFontScale';

import { Headline } from 'components/Headline';
import { Icon } from 'components/Icon';

import { ButtonMotives, pickMotive } from './buttonMotives';

export type StyledButtonProps = {
  motive?: ButtonMotives;
  scale?: FontScale;
  $isInline?: boolean;
};

export type StyledButtonIconProps = {
  icoarea?: string;
};

export type StyledLabelProps = {
  isFlexStretch?: boolean;
  whiteSpace?: 'normal' | 'nowrap';
  isBreadcrumbs?: boolean;
  lineHeight?: string;
  truncate?: boolean;
};

const Button = styled.button<StyledButtonProps>`
  ${({ $isInline }) =>
    $isInline
      ? css`
          display: inline-block;
        `
      : css`
          display: flex;
          align-items: center;
        `};

  margin: 0;
  padding: 0;
  height: 3.5rem;

  background-color: transparent;
  color: inherit;
  border: none;

  text-decoration: none;

  cursor: pointer;

  transition-property: color, background-color;
  transition: ${({ theme }) => theme.transitionSpeed} ease-in;

  ${({ motive }) => motive && pickMotive(motive)};
  ${({ scale }) => scale && getFontScale(scale)};

  &.disabled {
    background-color: ${pickColor('disabled')};
    color: ${pickColor('borderColorDarkSecondary')};
    border: none;

    cursor: not-allowed;
  }
`;

const IconLeft = styled(Icon)<StyledButtonIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1em;

  font-size: ${({ icoarea }) => `${icoarea}rem` || '1.2rem'};
  line-height: 1;
`;

const IconRight = styled(Icon)<StyledButtonIconProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1em;

  font-size: ${({ icoarea }) => `${icoarea}rem` || '1.2rem'};
  line-height: 1;
`;

const Label = styled(Headline)<StyledLabelProps>`
  flex: ${({ isFlexStretch }) => (isFlexStretch ? '1' : 'unset')};

  display: block;

  white-space: ${({ whiteSpace }) => whiteSpace};
  line-height: ${({ isBreadcrumbs, lineHeight }) =>
    lineHeight || (isBreadcrumbs ? 'unset' : 1)};

  ${({ truncate }) =>
    truncate &&
    css`
      overflow: hidden;
      min-width: 0;
      text-overflow: ellipsis;
    `}

  ${IconLeft} ~ & {
    padding-left: 0.3rem;
  }

  &:not(:last-of-type) {
    padding-right: 0.3rem;
  }
`;

export { Button, Label, IconLeft, IconRight };
